/** A sandbox's admins (DSEC D6): who "Open sandbox" from production can sign in as, and ops' "Add admin".
 *
 *  An admin is a staff user of the sandbox in a full-access or `operator_admin_*` group, or one recorded
 *  at creation and still waiting for that group (`pending`); production's "Open sandbox" signs in as the
 *  first active one. A sandbox created without its optional admin has none, and production's developers
 *  cannot open it until someone adds one (they can add the first one themselves; ops add any number here).
 *
 *  Adding needs `console_sandbox_manage`: a new dashboard user of the sandbox, its login the e-mail (no user
 *  of any tenant may have it). Without a password it has no usable password and signs in from production
 *  only. The request is sent straight from the form, never through `useMutation`: it may carry the
 *  admin's password (lesson "A credential must not ride useMutation"). */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import { sandboxApi, sandboxErrorMessage, sandboxKeys, type SandboxAdmin } from '@/api/sandbox';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { ADMIN_MEMBERSHIP_LABEL } from './sandboxWords';

const AddAdminSchema = z.object({
  email: z.string().trim().min(1, 'Required').email('Not an e-mail address'),
  first_name: z.string().trim().min(1, 'Required').max(150),
  last_name: z.string().trim().min(1, 'Required').max(150),
  password: z.string(),
});
type AddAdminValues = z.infer<typeof AddAdminSchema>;

const DEFAULTS: AddAdminValues = { email: '', first_name: '', last_name: '', password: '' };

/** The refusal's words, and the broken password rules (`password_weak`: `errors: [{field, error}]`). */
function refusalOf(e: unknown): { message: string; rules: string[] } {
  const body =
    e instanceof ConsoleApiError ? (e.body as Record<string, unknown> | undefined) : undefined;
  const rules = (Array.isArray(body?.errors) ? (body.errors as unknown[]) : [])
    .map((entry) =>
      typeof entry === 'string'
        ? entry
        : entry &&
            typeof entry === 'object' &&
            typeof (entry as { error?: unknown }).error === 'string'
          ? (entry as { error: string }).error
          : null,
    )
    .filter((rule): rule is string => Boolean(rule));
  const detail = typeof body?.detail === 'string' ? body.detail : '';
  return {
    message:
      rules.length && detail ? detail : sandboxErrorMessage(e, 'The admin could not be added.'),
    rules,
  };
}

function AdminsTable({ admins }: { admins: SandboxAdmin[] }) {
  return (
    <Table size="small" aria-label="Sandbox admins" data-testid="sandbox-admins">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>E-mail</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Admin rights</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {admins.map((admin) => (
          <TableRow key={admin.id}>
            <TableCell>{admin.name || '—'}</TableCell>
            <TableCell sx={{ wordBreak: 'break-all' }}>{admin.email}</TableCell>
            <TableCell>
              <Chip
                size="small"
                variant="outlined"
                color={admin.is_active ? 'success' : 'default'}
                label={admin.is_active ? 'Active' : 'Inactive'}
              />
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {ADMIN_MEMBERSHIP_LABEL[admin.membership] ?? admin.membership}
              </Typography>
              {!admin.password_set && (
                <Typography variant="caption" color="text.secondary" component="div">
                  Signs in from production only
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AddAdminDialog({
  open,
  topId,
  code,
  onClose,
}: {
  open: boolean;
  topId: number;
  code: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<{ message: string; rules: string[] } | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<AddAdminValues>({
    defaultValues: DEFAULTS,
    mode: 'onChange',
    resolver: zodResolver(AddAdminSchema),
  });

  const close = () => {
    // The password must not outlive the dialog.
    reset(DEFAULTS);
    setRefusal(null);
    onClose();
  };

  const onSubmit = async (v: AddAdminValues) => {
    setSubmitting(true);
    setRefusal(null);
    try {
      const { admin } = await sandboxApi.addAdmin(topId, {
        email: v.email.trim(),
        first_name: v.first_name.trim(),
        last_name: v.last_name.trim(),
        password: v.password ? v.password : null,
      });
      snackbar.show(`${admin.name || admin.email} was added as an admin of ${code}.`, 'success');
      void qc.invalidateQueries({ queryKey: sandboxKeys.state(topId) });
      close();
    } catch (e) {
      setRefusal(refusalOf(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={close}
      title={`Add an admin to ${code}`}
      disableBackdropClose
      closeDisabled={submitting}
      actions={
        <>
          <Button onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="sandbox-add-admin"
            disabled={!isValid || submitting}
            startIcon={submitting ? <CircularProgress size={14} /> : undefined}
          >
            Add admin
          </Button>
        </>
      }
    >
      <Box
        component="form"
        id="sandbox-add-admin"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'grid', gap: 2, pt: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          A new dashboard user of the sandbox, in its admin group. Its login is the e-mail, which no
          user of any tenant may have yet.
        </Typography>
        {refusal && (
          <Alert severity="error">
            {refusal.message}
            {refusal.rules.length > 0 && (
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {refusal.rules.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        <TextField
          size="small"
          label="E-mail"
          required
          autoComplete="off"
          {...register('email')}
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
        />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField
            size="small"
            label="First name"
            required
            {...register('first_name')}
            error={Boolean(errors.first_name)}
            helperText={errors.first_name?.message}
          />
          <TextField
            size="small"
            label="Last name"
            required
            {...register('last_name')}
            error={Boolean(errors.last_name)}
            helperText={errors.last_name?.message}
          />
        </Box>
        <TextField
          size="small"
          label="Password (optional)"
          type="password"
          autoComplete="new-password"
          {...register('password')}
          helperText="Without a password the admin signs in from production only (“Open sandbox”). With one, also on the sandbox’s own dashboard."
        />
      </Box>
    </AppDialog>
  );
}

export function SandboxAdminsSection({
  topId,
  code,
  admins,
  canManage,
}: {
  topId: number;
  code: string;
  admins: SandboxAdmin[] | null | undefined;
  canManage: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const list = admins ?? [];
  return (
    <Stack spacing={1.5}>
      {list.length > 0 ? (
        <AdminsTable admins={list} />
      ) : (
        <Alert severity="info">
          No admin: “Open sandbox” from production has nobody to sign in as. Its developers can add
          the first one from the production dashboard, or add one here.
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        “Open sandbox” from production signs in as the first active admin; “Open sandbox as…” lists
        every active staff user.
      </Typography>
      {canManage && (
        <Box>
          <Button variant="outlined" onClick={() => setAdding(true)}>
            Add admin…
          </Button>
        </Box>
      )}
      {canManage && (
        <AddAdminDialog open={adding} topId={topId} code={code} onClose={() => setAdding(false)} />
      )}
    </Stack>
  );
}
